import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CostCentreIndexEntry,
  InkProfileIndexEntry,
  MaterialIndexEntry,
  OutworkChoiceEntry,
  ProcessTypeEntry,
  ProductTypeIndexEntry,
  SiteEntry,
} from "@shared-core/data/manufacturing-setup-types";
import {
  formatEngineUseSummary,
  formatChangedInTharstern,
  siteTownName,
} from "@shared-core/domain/manufacturing-setup-presentation";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Badge } from "../app/components/primitives/badge";
import { Input } from "../app/components/primitives/input";
import { Tabs, TabsList, TabsTrigger } from "../app/components/primitives/tabs";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Factory, Search } from "lucide-react";

const PAGE_SIZE = 25;

type Family =
  | "cost-centres"
  | "ink-profiles"
  | "product-types"
  | "materials"
  | "outwork"
  | "sites"
  | "process-types";

const FAMILIES: { value: Family; label: string }[] = [
  { value: "cost-centres", label: "Cost centres" },
  { value: "ink-profiles", label: "Ink profiles" },
  { value: "product-types", label: "Product types" },
  { value: "materials", label: "Materials" },
  { value: "outwork", label: "Outwork" },
  { value: "sites", label: "Sites" },
  { value: "process-types", label: "Process types" },
];

/** Generic loading / error / empty body — every family view shares the same
 *  three non-happy states, per "Read it as Henry": say why, don't just
 *  vanish. */
function TableStateRow({
  colSpan,
  children,
  tone = "muted",
}: {
  colSpan: number;
  children: React.ReactNode;
  tone?: "muted" | "destructive";
}) {
  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        className={`text-center py-8 ${tone === "destructive" ? "text-destructive" : "text-muted-foreground"}`}
      >
        {children}
      </TableCell>
    </TableRow>
  );
}

function EngineUseSummary({ used, total }: { used: number; total: number }) {
  if (total === 0) {
    return (
      <span className="text-xs text-muted-foreground">not yet detailed</span>
    );
  }
  return (
    <span className="text-xs text-muted-foreground">
      {formatEngineUseSummary(used, total)}
    </span>
  );
}

// ── Cost centres ────────────────────────────────────────────────────────

function CostCentresView() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [availability, setAvailability] = useState<
    "" | "available" | "unavailable"
  >("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<CostCentreIndexEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    adapter
      .listManufacturingCostCentres({
        page,
        pageSize: PAGE_SIZE,
        q: debouncedQ || undefined,
        availability: availability || undefined,
      })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setTotalPages(res.totalPages);
        setFirstLoad(false);
      })
      .catch(() => {
        setLoadError(true);
        setFirstLoad(false);
      });
  }, [adapter, page, debouncedQ, availability]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[12rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by code or name..."
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>
        <select
          aria-label="Filter by availability"
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={availability}
          onChange={(e) => {
            setAvailability(e.target.value as typeof availability);
            setPage(1);
          }}
        >
          <option value="">All availability</option>
          <option value="available">Available</option>
          <option value="unavailable">Unavailable</option>
        </select>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cost centre</TableHead>
              <TableHead>Process type</TableHead>
              <TableHead>Site</TableHead>
              <TableHead>Availability</TableHead>
              <TableHead>Engine use</TableHead>
              <TableHead>Changed in Tharstern</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {firstLoad ? (
              <TableStateRow colSpan={6}>Loading…</TableStateRow>
            ) : loadError ? (
              <TableStateRow colSpan={6} tone="destructive">
                Couldn&rsquo;t load cost centres. Please try again.
              </TableStateRow>
            ) : items.length === 0 ? (
              <TableStateRow colSpan={6}>
                No cost centres match these filters
              </TableStateRow>
            ) : (
              items.map((cc) => (
                <TableRow
                  key={cc.costCentreId}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(
                      paths.settingsLiveCostingCostCentreDetail({
                        costCentreId: cc.costCentreId,
                      }),
                    )
                  }
                >
                  <TableCell className="font-medium">
                    {cc.costCentreId} {cc.code}
                    {cc.name !== cc.code && (
                      <div className="text-xs font-normal text-muted-foreground">
                        {cc.name}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {cc.processTypeName}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {siteTownName(cc.site)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={cc.isAvailable ? "success" : "neutral"}>
                      {cc.isAvailable ? "Available" : "Unavailable"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <EngineUseSummary
                      used={cc.engineUseUsedCount}
                      total={cc.engineUseTotalCount}
                    />
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {formatChangedInTharstern(cc.changedInTharsternAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
      {!firstLoad && !loadError && (
        <p className="text-xs text-muted-foreground">
          {total} {total === 1 ? "cost centre" : "cost centres"} · synced 03:12
          from <code>CostCentres</code>
        </p>
      )}
    </div>
  );
}

// ── Ink profiles ────────────────────────────────────────────────────────

function InkProfilesView() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<InkProfileIndexEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    adapter
      .listManufacturingInkProfiles({
        page,
        pageSize: PAGE_SIZE,
        q: debouncedQ || undefined,
      })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setTotalPages(res.totalPages);
        setFirstLoad(false);
      })
      .catch(() => {
        setLoadError(true);
        setFirstLoad(false);
      });
  }, [adapter, page, debouncedQ]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by code or name..."
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="pl-9"
        />
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ink profile</TableHead>
              <TableHead>Process / spot / metallic</TableHead>
              <TableHead>Coatings</TableHead>
              <TableHead>Legacy</TableHead>
              <TableHead>Suitable presses</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {firstLoad ? (
              <TableStateRow colSpan={5}>Loading…</TableStateRow>
            ) : loadError ? (
              <TableStateRow colSpan={5} tone="destructive">
                Couldn&rsquo;t load ink profiles. Please try again.
              </TableStateRow>
            ) : items.length === 0 ? (
              <TableStateRow colSpan={5}>
                No ink profiles match this search
              </TableStateRow>
            ) : (
              items.map((p) => (
                <TableRow
                  key={p.inkProfileId}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(
                      paths.settingsLiveCostingInkProfileDetail({
                        inkProfileId: p.inkProfileId,
                      }),
                    )
                  }
                >
                  <TableCell className="font-medium">
                    {p.code}
                    <div className="text-xs font-normal text-muted-foreground">
                      {p.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {p.processInks} / {p.spotInks} / {p.metallicInks}
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {p.coatings}
                  </TableCell>
                  <TableCell>
                    {p.isLegacy ? (
                      <Badge variant="neutral">Legacy</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {p.suitablePressCount}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
      {!firstLoad && !loadError && (
        <p className="text-xs text-muted-foreground">
          {total} {total === 1 ? "ink profile" : "ink profiles"} · synced 03:12
          from <code>InkProfile</code>
        </p>
      )}
    </div>
  );
}

// ── Product types ───────────────────────────────────────────────────────

function ProductTypesView() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [items, setItems] = useState<ProductTypeIndexEntry[]>([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    adapter
      .listManufacturingProductTypes({
        q: debouncedQ || undefined,
        pageSize: 200,
      })
      .then((res) => {
        setItems(res.items);
        setFirstLoad(false);
      })
      .catch(() => {
        setLoadError(true);
        setFirstLoad(false);
      });
  }, [adapter, debouncedQ]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
        />
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product type</TableHead>
              <TableHead>Order expense</TableHead>
              <TableHead>Parts</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {firstLoad ? (
              <TableStateRow colSpan={4}>Loading…</TableStateRow>
            ) : loadError ? (
              <TableStateRow colSpan={4} tone="destructive">
                Couldn&rsquo;t load product types. Please try again.
              </TableStateRow>
            ) : items.length === 0 ? (
              <TableStateRow colSpan={4}>
                No product types match this search
              </TableStateRow>
            ) : (
              items.map((pt) => (
                <TableRow
                  key={pt.productTypeId}
                  className={`cursor-pointer ${pt.isReplicated ? "" : "opacity-60"}`}
                  onClick={() =>
                    navigate(
                      paths.settingsLiveCostingProductTypeDetail({
                        productTypeId: pt.productTypeId,
                      }),
                    )
                  }
                >
                  <TableCell className="font-medium">
                    {pt.productTypeId} · {pt.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatCurrencyGbp(pt.orderExpensePence / 100, 2)}
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {pt.partCount || "—"}
                  </TableCell>
                  <TableCell>
                    {pt.isReplicated ? (
                      <Badge variant="success">Replicated</Badge>
                    ) : (
                      <Badge variant="outline">Not yet replicated</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {!firstLoad && !loadError && (
        <p className="text-xs text-muted-foreground">
          {items.length} product {items.length === 1 ? "type" : "types"} ·
          synced 10 Sep 04:00 from <code>ProductTypeProcessTypeLinks</code>
        </p>
      )}
    </div>
  );
}

// ── Materials ───────────────────────────────────────────────────────────

function MaterialsView() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<MaterialIndexEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    adapter
      .listManufacturingMaterials({
        page,
        pageSize: PAGE_SIZE,
        q: debouncedQ || undefined,
      })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setTotalPages(res.totalPages);
        setFirstLoad(false);
      })
      .catch(() => {
        setLoadError(true);
        setFirstLoad(false);
      });
  }, [adapter, page, debouncedQ]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by material code…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="pl-9"
        />
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Material code</TableHead>
              <TableHead>Weight</TableHead>
              <TableHead>Sheet sizes</TableHead>
              <TableHead>Group / Type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {firstLoad ? (
              <TableStateRow colSpan={4}>Loading…</TableStateRow>
            ) : loadError ? (
              <TableStateRow colSpan={4} tone="destructive">
                Couldn&rsquo;t load materials. Please try again.
              </TableStateRow>
            ) : items.length === 0 ? (
              <TableStateRow colSpan={4}>
                No materials match this search
              </TableStateRow>
            ) : (
              items.map((m) => (
                <TableRow
                  key={m.code}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(
                      paths.settingsLiveCostingMaterialDetail({
                        code: m.code,
                      }),
                    )
                  }
                >
                  <TableCell className="font-medium">{m.code}</TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {m.gsmOrMicrons} {m.gsmOrMicronsUnit}
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {m.sheetSizeCount}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {m.group} / {m.type}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
      {!firstLoad && !loadError && (
        <p className="text-xs text-muted-foreground">
          {total} {total === 1 ? "material" : "materials"} · synced 03:12 from{" "}
          <code>Product</code>
        </p>
      )}
    </div>
  );
}

// ── Outwork ─────────────────────────────────────────────────────────────

function OutworkView() {
  const adapter = useDataAdapter();
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [items, setItems] = useState<OutworkChoiceEntry[]>([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    adapter
      .listManufacturingOutworkChoices({
        q: debouncedQ || undefined,
        pageSize: 200,
      })
      .then((res) => {
        setItems(res.items);
        setFirstLoad(false);
      })
      .catch(() => {
        setLoadError(true);
        setFirstLoad(false);
      });
  }, [adapter, debouncedQ]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by outwork code..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
        />
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Outwork choice</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Min charge</TableHead>
              <TableHead>Additional charge</TableHead>
              <TableHead>Price bands</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {firstLoad ? (
              <TableStateRow colSpan={5}>Loading…</TableStateRow>
            ) : loadError ? (
              <TableStateRow colSpan={5} tone="destructive">
                Couldn&rsquo;t load outwork choices. Please try again.
              </TableStateRow>
            ) : items.length === 0 ? (
              <TableStateRow colSpan={5}>
                No outwork choices match this search
              </TableStateRow>
            ) : (
              items.map((o) => (
                <TableRow key={o.outworkId}>
                  <TableCell className="font-medium">{o.code}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {o.supplierCode}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatCurrencyGbp(o.minChargePence / 100, 2)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {o.additionalChargePence > 0
                      ? formatCurrencyGbp(o.additionalChargePence / 100, 2)
                      : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {o.priceBands
                      .map(
                        (b) =>
                          `${b.sizeCode} ${formatCurrencyGbp(b.pricePence / 100, 2)}`,
                      )
                      .join(" · ")}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {!firstLoad && !loadError && (
        <p className="text-xs text-muted-foreground">
          {items.length} outwork {items.length === 1 ? "choice" : "choices"} ·
          synced 11 Sep 22:05 from <code>OutworkChoices</code>
        </p>
      )}
    </div>
  );
}

// ── Sites & process types (simple lists) ────────────────────────────────

function SitesView() {
  const adapter = useDataAdapter();
  const [items, setItems] = useState<SiteEntry[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    adapter
      .listManufacturingSites({ pageSize: 200 })
      .then((res) => setItems(res.items))
      .catch(() => setLoadError(true));
  }, [adapter]);

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Site</TableHead>
              <TableHead>Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadError ? (
              <TableStateRow colSpan={2} tone="destructive">
                Couldn&rsquo;t load sites. Please try again.
              </TableStateRow>
            ) : items === null ? (
              <TableStateRow colSpan={2}>Loading…</TableStateRow>
            ) : items.length === 0 ? (
              <TableStateRow colSpan={2}>No sites replicated</TableStateRow>
            ) : (
              items.map((s) => (
                <TableRow key={s.siteId}>
                  <TableCell className="font-medium">{s.code}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {s.description}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {items !== null && !loadError && (
        <p className="text-xs text-muted-foreground">
          {items.length} {items.length === 1 ? "site" : "sites"} · synced 1 Sep
          04:00 from <code>LocationSites</code>
        </p>
      )}
    </div>
  );
}

function ProcessTypesView() {
  const adapter = useDataAdapter();
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [items, setItems] = useState<ProcessTypeEntry[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    adapter
      .listManufacturingProcessTypes({
        q: debouncedQ || undefined,
        pageSize: 200,
      })
      .then((res) => setItems(res.items))
      .catch(() => setLoadError(true));
  }, [adapter, debouncedQ]);

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by process type name..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
        />
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Process type</TableHead>
              <TableHead>Cost centres using this process type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadError ? (
              <TableStateRow colSpan={2} tone="destructive">
                Couldn&rsquo;t load process types. Please try again.
              </TableStateRow>
            ) : items === null ? (
              <TableStateRow colSpan={2}>Loading…</TableStateRow>
            ) : items.length === 0 ? (
              <TableStateRow colSpan={2}>
                No process types match this search
              </TableStateRow>
            ) : (
              items.map((p) => (
                <TableRow key={p.processTypeId}>
                  <TableCell className="font-medium">
                    {p.processTypeId} · {p.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {p.costCentreCount}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {items !== null && !loadError && (
        <p className="text-xs text-muted-foreground">
          {items.length} {items.length === 1 ? "process type" : "process types"}{" "}
          · synced 1 Sep 04:00 from <code>ProcessTypes</code>
        </p>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function ManufacturingSetupPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const family = (searchParams.get("family") as Family) || "cost-centres";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
          <Factory className="h-6 w-6" /> Manufacturing setup
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          A read-only mirror of the manufacturing configuration Tharstern
          masters — sites, cost centres, ink profiles, product types, materials,
          outwork and process types — with what the cost engine does with each
          field.
        </p>
      </div>

      <Tabs
        value={family}
        onValueChange={(v) =>
          setSearchParams(
            (prev) => {
              const params = new URLSearchParams(prev);
              if (v === "cost-centres") params.delete("family");
              else params.set("family", v);
              return params;
            },
            { replace: true },
          )
        }
      >
        <TabsList>
          {FAMILIES.map((f) => (
            <TabsTrigger key={f.value} value={f.value}>
              {f.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {family === "cost-centres" && <CostCentresView />}
      {family === "ink-profiles" && <InkProfilesView />}
      {family === "product-types" && <ProductTypesView />}
      {family === "materials" && <MaterialsView />}
      {family === "outwork" && <OutworkView />}
      {family === "sites" && <SitesView />}
      {family === "process-types" && <ProcessTypesView />}
    </div>
  );
}
